import React from 'react'
import { Box, IconButton, Typography } from '@mui/material'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import CloseIcon from '@mui/icons-material/Close'
import { C, tabular } from './tokens'
import { hourLong, money, pct, spanDays } from './format'

/** "last week", "yesterday", "the previous 12 days", "the same period last year". */
function comparisonPhrase(compare, from, to) {
  if (compare === 'year') return 'the same period last year'
  const n = spanDays(from, to)
  if (n === 1) return 'yesterday'
  if (n === 7) return 'last week'
  if (n === 30) return 'the previous 30 days'
  return `the previous ${n} days`
}

/** Mon, Mon–Wed — a run of weekdays as the kitchen would say it. */
const runLabel = (days, a, b) => (a === b ? days[a].weekday : `${days[a].weekday}–${days[b].weekday}`)

/**
 * The run of consecutive days that moved sales most in `sign`'s direction
 * (−1 = biggest drop, +1 = biggest gain). Closed days break a run.
 */
function biggestRun(days, sign) {
  let best = null
  let start = 0
  let sum = 0
  days.forEach((d, i) => {
    const diff = d.closed || d.prevSales == null ? 0 : (d.sales - d.prevSales) * sign
    if (diff > 0) {
      if (sum === 0) start = i
      sum += diff
      if (!best || sum > best.sum) best = { a: start, b: i, sum }
    } else {
      sum = 0
    }
  })
  return best
}

/** One sentence about the period, from the data only — no guessing. */
export function insightFor(data, compare) {
  if (!data) return null
  const cur = data.current
  const prev = data.previous
  const days = data.days || []

  if (cur.orders === 0) {
    return prev && prev.orders > 0
      ? `No orders yet in this range — ${comparisonPhrase(compare, cur.from, cur.to)} had ${prev.orders} (${money(prev.grossSales)}).`
      : null
  }

  if (prev && prev.grossSales > 0) {
    const diff = cur.grossSales - prev.grossSales
    const phrase = comparisonPhrase(compare, cur.from, cur.to)
    if (Math.abs(diff) < 0.005) return `Sales are level with ${phrase} at ${money(cur.grossSales)}.`
    const dir = diff < 0 ? 'down' : 'up'
    let s = `Sales are ${dir} ${money(Math.abs(diff))} (${pct((diff / prev.grossSales) * 100)}) vs ${phrase}.`
    const run = days.length > 1 ? biggestRun(days, diff < 0 ? -1 : 1) : null
    if (run) s += ` The biggest ${diff < 0 ? 'drop' : 'gain'} was ${runLabel(days, run.a, run.b)}.`
    return s
  }

  const bestDay = days.reduce((b, d) => (d.sales > (b?.sales ?? 0) ? d : b), null)
  const peak = (data.hourly || []).reduce((b, h) => (h.sales > (b?.sales ?? 0) ? h : b), null)
  const parts = []
  if (bestDay && days.length > 1) parts.push(`Best day: ${bestDay.weekday} (${money(bestDay.sales)}).`)
  if (peak && peak.sales > 0) parts.push(`Peak hour: ${hourLong(peak.hour)}.`)
  return parts.length ? parts.join(' ') : null
}

export default function Insight({ text, onDismiss }) {
  if (!text) return null
  return (
    <Box
      role="status"
      sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.25, borderRadius: '12px', bgcolor: 'var(--brand-soft)', border: `1px solid rgba(237,29,38,0.14)` }}>
      <AutoAwesomeOutlinedIcon sx={{ fontSize: 18, color: C.brandText, flexShrink: 0 }} aria-hidden />
      <Typography sx={{ fontSize: 14, color: C.text, flex: 1, ...tabular }}>{text}</Typography>
      <IconButton size="small" onClick={onDismiss} aria-label="Dismiss insight" sx={{ color: C.textMuted, '&:hover': { bgcolor: 'rgba(17,24,39,0.06)' } }}>
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  )
}
